/*
 * Abonelik Yenile Modalı
 * Seçili cihaz için yeni bir döngü tarihi belirleyip backend'deki
 * /api/abonelikler/{abonelik_id} PUT endpoint'ine istek atar.
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  Text,
  HStack,
  VStack,
  useToast,
  Box,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import api from "utils/api";

export default function AbonelikYenileModal({
  isOpen,
  onClose,
  abonelik,
  onSuccess,
}) {
  const toast = useToast();
  const [plan, setPlan] = useState("12");
  const [saving, setSaving] = useState(false);
  const [simAskOpen, setSimAskOpen] = useState(false);
  const simCancelRef = useRef(null);

  useEffect(() => {
    if (isOpen && abonelik) {
      if (abonelik?.tur) {
        setPlan(String(abonelik.tur));
      } else {
        setPlan("12");
      }
    }
  }, [isOpen, abonelik]);

  useEffect(() => {
    if (!isOpen) setSimAskOpen(false);
  }, [isOpen]);

  const computeNewDonguTarihi = () => {
    const months = parseInt(plan || "12", 10);
    const today = new Date();

    let baseDate = today;
    if (abonelik?.dongu_tarihi_raw) {
      const raw = new Date(abonelik.dongu_tarihi_raw);
      if (!Number.isNaN(raw.getTime()) && raw > today) {
        baseDate = raw;
      }
    }

    const d = new Date(baseDate);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    if (d.getDate() < day) {
      d.setDate(0);
    }

    return d.toISOString().split("T")[0];
  };

  const planLabels = {
    "1": "1 Ay",
    "3": "3 Ay",
    "12": "12 Ay",
    "24": "24 Ay",
  };

  /** @param {boolean|undefined} restoreBosaCikanSim true/false = diyalog cevabı; undefined = alan gönderme (backend varsayılan: boşa çıkan SIM’i kullanımda yap) */
  const submitRenew = async (restoreBosaCikanSim) => {
    if (!abonelik?.abonelik_id) {
      toast({
        title: "Abonelik bulunamadı",
        description: "Bu kayıt için abonelik ID bilgisi eksik.",
        status: "error",
      });
      return;
    }

    try {
      setSaving(true);
      const newDonguTarihi = computeNewDonguTarihi();
      const payload = {
        dongu_tarihi: newDonguTarihi,
        tur: plan,
        abonelik_tipi: planLabels[plan] || `${plan} Ay`,
      };
      if (restoreBosaCikanSim === true) {
        payload.restore_bosa_cikan_sim = true;
      } else if (restoreBosaCikanSim === false) {
        payload.restore_bosa_cikan_sim = false;
      }

      const res = await api.put(
        `/api/abonelikler/${abonelik.abonelik_id}`,
        payload
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || "Abonelik yenilenemedi.");
      }

      toast({
        title: "Başarılı",
        description: "Abonelik süresi güncellendi.",
        status: "success",
      });

      onSuccess?.(data);
      onClose();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!abonelik?.abonelik_id) {
      toast({
        title: "Abonelik bulunamadı",
        description: "Bu kayıt için abonelik ID bilgisi eksik.",
        status: "error",
      });
      return;
    }
    if (abonelik.sim_bosa_cikan) {
      setSimAskOpen(true);
      return;
    }
    submitRenew();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" boxShadow="xl" maxW="520px" bg="white" p={1}>
          <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
            <ModalCloseButton size="sm" />
            <VStack spacing={1} align="start">
              <Text fontSize="lg" fontWeight="medium" color="gray.800">
                Abonelik Yenile
              </Text>
              <Text fontSize="xs" color="gray.600">
                {abonelik?.plaka || "Araç"} için abonelik yenileyin
              </Text>
            </VStack>
          </Box>
          <ModalBody px={6} py={4}>
            {abonelik ? (
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Plaka
                  </Text>
                  <Text fontSize="sm" fontWeight="600">
                    {abonelik.plaka || "-"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    IMEI
                  </Text>
                  <Text fontSize="sm" fontWeight="600">
                    {abonelik.imei || "-"}
                  </Text>
                </HStack>
                {abonelik.sim_no ? (
                  <HStack justify="space-between" align="start">
                    <Text fontSize="sm" color="gray.600">
                      SIM
                    </Text>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="sm" fontWeight="600">
                        {abonelik.sim_no}
                      </Text>
                      {abonelik.sim_bosa_cikan ? (
                        <Badge colorScheme="orange" fontSize="xs">
                          SIM yönetiminde boşa çıkmış
                        </Badge>
                      ) : null}
                    </VStack>
                  </HStack>
                ) : null}
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">
                    Yeni Abonelik Süresi
                  </FormLabel>
                  <Select
                    variant="flushed"
                    size="sm"
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                  >
                    <option value="1">1 Ay</option>
                    <option value="3">3 Ay</option>
                    <option value="12">12 Ay</option>
                    <option value="24">24 Ay</option>
                  </Select>
                </FormControl>
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.500">
                Abonelik bilgisi bulunamadı.
              </Text>
            )}
          </ModalBody>
          <ModalFooter px={6} py={4}>
            <Button variant="ghost" mr={3} onClick={onClose} size="sm">
              İptal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={saving}
              size="sm"
            >
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={simAskOpen}
        leastDestructiveRef={simCancelRef}
        onClose={() => setSimAskOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md" fontWeight="bold">
              Eski SIM ile devam mı?
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Bu araçtaki numara ({abonelik?.sim_no || "—"}), abonelik süresi dolduğu için SIM
              yönetiminde <strong>boşa çıkan</strong> olarak işaretlenmiş olabilir. Aboneliği
              yenilediğinizde aynı SIM ile devam edecekseniz <strong>Evet</strong> deyin; SIM tekrar{" "}
              <strong>kullanımda</strong> olarak güncellenir. Farklı bir SIM kullanacaksanız{" "}
              <strong>Hayır</strong> deyin (SIM yönetimini siz manuel düzenlersiniz).
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={simCancelRef} onClick={() => setSimAskOpen(false)} size="sm">
                İptal
              </Button>
              <Button
                size="sm"
                mr={3}
                onClick={() => {
                  setSimAskOpen(false);
                  submitRenew(false);
                }}
              >
                Hayır
              </Button>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={() => {
                  setSimAskOpen(false);
                  submitRenew(true);
                }}
              >
                Evet, eski SIM ile devam
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
